import { useContext, useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DEFAULT_BLOCK_BORDER_COLOR } from '../../../../shared/documentColorDefaults';
import type { RootState } from '../../../../grid/reduxStore/Store';
import { useAppDispatch } from '../../../../grid/reduxStore/Store';

import type { SelectChangeEvent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';
import IcoLock from '../../../../../icon/icons/IcoLock';
import IcoLockOpen from '../../../../../icon/icons/IcoLockOpen';
import { EditorDefaultSidePanelType, setActiveSidePanel, ShapeType } from '../../../../grid/reduxStore/blockStyleSettingsSlice';
import { DEFAULT_SHAPE_BACKGROUND_COLOR } from '../../../../shared/documentColorDefaults';
import { type BlockContent, selectSectionIdByBlockId } from 'components/editor/grid/reduxStore/editorSlice';
import { SecondaryMenu, SecondaryMenuSectionTitle, NumInputBoxTwoCol, NumberInputBoxSingleCol } from '../../../component';
import { SelectionContext } from '../../../../GridDndEditor/SelectedBlockInfoProvider';
import { useBlockContentWithSectionId } from 'components/editor/Sections/useBlockContent';
import PySelectInput from '../../../../shared/components/PyMuiComponents/PySelectInput.tsx';
import { DIMENSION_HEIGHT, DIMENSION_WIDTH } from 'components/editor/SidePanel/content/DesignSettings/common/useAspectRatio.ts';
import { useNewBlockContentChangedHandler } from '../../../../hooks/useNewBlockContentChangedHandler.ts';
import { useNewBlockStyleSettings } from '../../../../hooks/useNewBlockSettings.ts';

import { ColorPickerWithUndoRedoSection } from '../../../component/ColorPickerWithundoRedoSection.tsx';
import { useNewAspectRatio } from 'components/editor/SidePanel/content/DesignSettings/common/useNewAspectRatio.ts';

export function ShapeWithUndoRedoBlockSettings() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;
  const sectionId = useSelector((state: RootState) => selectSectionIdByBlockId(state, blockId));
  const blockContent = useBlockContentWithSectionId(sectionId ?? '', blockId) as BlockContent;
  const shapeType = blockContent?.content || ShapeType.SQUARE;
  const newBlockStyleSettings = useNewBlockStyleSettings(blockId, sectionId ?? '');

  const {
    getBlockStyle,
    changeBorderWidthInUi,
    updateBlockStyleWithUndo,
    changeBorderRadius,
    changeBorderWidth,
    updateBlockStyleInUi,
    saveCurrentStateToRef,
    changeBorderRadiusInUi,
  } = newBlockStyleSettings;
  const { handleBlockContentChanged } = useNewBlockContentChangedHandler();
  const shapeOptions = [
    { label: t('document.grid.shapes.design.square'), value: ShapeType.SQUARE },
    { label: t('document.grid.shapes.design.circle'), value: ShapeType.CIRCLE },
    { label: t('document.grid.shapes.design.triangle'), value: ShapeType.TRIANGLE },
  ];
  const width = blockContent?.blockConfig.width || 0;
  const height = blockContent?.blockConfig.height || 0;

  const {
    imageAspectRatioLock = false,
    backgroundColor = DEFAULT_SHAPE_BACKGROUND_COLOR,
    borderColor = DEFAULT_BLOCK_BORDER_COLOR,
    borderRadius = 0,
    borderWidth = 0,
  } = useMemo(() => getBlockStyle(blockId), [getBlockStyle, selectedBlockIdByWrapper]);

  const { handleImageDimensionChange } = useNewAspectRatio({
    imageAspectRatioLock,
    sectionId: sectionId ?? '',
    blockId,
  });

  const handleFocus = useCallback(() => {
    saveCurrentStateToRef(getBlockStyle(blockId));
  }, [getBlockStyle, saveCurrentStateToRef]);

  const [typedWidth, setTypedWidth] = useState<number | null>(null);
  const [typedHeight, setTypedHeight] = useState<number | null>(null);

  const originalDimensionsRef = useRef<{
    width: number;
    height: number;
    blockId: string;
    blockContent: BlockContent;
  } | null>(null);

  useEffect(() => {
    setTypedWidth(null);
    setTypedHeight(null);
    originalDimensionsRef.current = null;
  }, [blockId]);

  const handleDimensionFocus = useCallback(() => {
    originalDimensionsRef.current = {
      width,
      height,
      blockId,
      blockContent: {
        ...blockContent,
        blockConfig: { ...blockContent.blockConfig },
      },
    };
  }, [width, height, blockId, blockContent]);

  const handleWidthChange = (newWidth: number) => {
    setTypedWidth(newWidth);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, newWidth, false, originalDimensionsRef.current);
    }
  };

  const handleWidthBlur = () => {
    if (typedWidth !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_WIDTH, typedWidth, true, originalDimensionsRef.current);
    }

    setTypedWidth(null);
  };

  const handleHeightChange = (newHeight: number) => {
    setTypedHeight(newHeight);

    if (originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, newHeight, false, originalDimensionsRef.current);
    }
  };

  const handleHeightBlur = () => {
    if (typedHeight !== null && originalDimensionsRef.current) {
      handleImageDimensionChange(DIMENSION_HEIGHT, typedHeight, true, originalDimensionsRef.current);
    }

    setTypedHeight(null);
  };

  const handleShapeTypeChange = (event: SelectChangeEvent<unknown>): void => {
    const type = event.target.value as ShapeType;
    sectionId && handleBlockContentChanged(blockId, sectionId, shapeType, type);
  };

  const handleBackButtonClick = useCallback(() => {
    dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  }, [dispatch]);

  return (
    <SecondaryMenu
      testId={'shape-block-settings-component'}
      menuTitle={t('document.grid.shapes.design.title')}
      backButtonOnClick={handleBackButtonClick}
    >
      <Stack rowGap="16px" width="100%">
        <Stack gap={1} direction="row" alignItems="end">
          <Stack direction="row" gap={2} width="100%">
            <FormControl sx={{ mb: 1, minWidth: 'none', mt: '10px' }} size="small" fullWidth>
              <PySelectInput
                id={`${t('document.grid.shapes.design.shape_type')}-id`}
                data-testid={'data-test-shape-type-select'}
                fullWidth
                label={t('document.grid.shapes.design.shape_type')}
                labelId={`${t('document.grid.shapes.design.shape_type')}-id`}
                MenuProps={{ disableScrollLock: true }}
                notched={true}
                onChange={handleShapeTypeChange}
                value={shapeType}
              >
                {shapeOptions?.map((opt) => (
                  <MenuItem data-testid={`data-test-shape-option-${opt.label.toLocaleLowerCase()}`} key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </PySelectInput>
            </FormControl>
          </Stack>
        </Stack>
      </Stack>
      <Stack rowGap="16px" width="100%">
        <Stack gap={1} direction="row" alignItems="end">
          <Stack direction="row" gap={2}>
            <NumberInputBoxSingleCol
              title={t('document.grid.image.design.width')}
              onChange={handleWidthChange}
              onBlur={handleWidthBlur}
              onFocus={handleDimensionFocus}
              value={width}
              endAdornmentText="px"
              testId="image-width-input"
            />
            <NumberInputBoxSingleCol
              title={t('document.grid.image.design.height')}
              onChange={handleHeightChange}
              onBlur={handleHeightBlur}
              onFocus={handleDimensionFocus}
              value={height}
              endAdornmentText="px"
              testId="image-height-input"
            />
          </Stack>

          <IconButton
            data-testid="aspect-ratio-lock-button"
            onClick={() => updateBlockStyleWithUndo({ imageAspectRatioLock: !imageAspectRatioLock })}
          >
            {imageAspectRatioLock ? <IcoLock /> : <IcoLockOpen />}
          </IconButton>
        </Stack>
      </Stack>
      <Stack rowGap="16px" width="100%">
        <ColorPickerWithUndoRedoSection
          currentColor={backgroundColor}
          onOpen={handleFocus}
          onColorChange={(backgroundColor) => updateBlockStyleInUi({ backgroundColor })}
          onClose={(backgroundColor: string) => updateBlockStyleWithUndo({ backgroundColor })}
        />
        <SecondaryMenuSectionTitle hasUnderLine={true}>{t('document.grid.shapes.design.borders')}</SecondaryMenuSectionTitle>
        <NumInputBoxTwoCol
          inputValue={borderWidth}
          onFocus={handleFocus}
          onChange={(borderWidth: number) => changeBorderWidthInUi(borderWidth)}
          onBlur={(borderWidth: number) => changeBorderWidth(borderWidth)}
          title={t('document.grid.shapes.design.border_width')}
          testId={'border-width'}
          disableNegativeNum
        />
        <NumInputBoxTwoCol
          title={t('document.grid.shapes.design.border_radius')}
          inputValue={borderRadius}
          disabled={shapeType === ShapeType.TRIANGLE || shapeType === ShapeType.CIRCLE}
          disableNegativeNum
          onFocus={handleFocus}
          onChange={(borderRadius) => changeBorderRadiusInUi({ radius: borderRadius })}
          onBlur={(borderRadius) => changeBorderRadius({ radius: borderRadius })}
          testId={'border-radius'}
        />
        <ColorPickerWithUndoRedoSection
          currentColor={borderColor}
          onOpen={handleFocus}
          onColorChange={(borderColor) => updateBlockStyleInUi({ borderColor })}
          onClose={(borderColor: string) => updateBlockStyleWithUndo({ borderColor })}
        />
      </Stack>
    </SecondaryMenu>
  );
}
